import { Flag } from '../components/shared/Flag';
import { useState, useEffect } from 'react';
import {
  Text, Button, Spinner, Input,
  makeStyles, tokens, mergeClasses, Checkbox, Dropdown, Option, Switch,
  Radio, RadioGroup,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  fetchAiProvidersConfig, updateAiProvidersConfig,
  fetchSystemBehavior, updateSystemBehavior,
  type AiProvidersConfig, type AiProviderId, type SystemBehaviorConfig,
} from '../api/rancho.api';
import {
  fetchAgroModulesConfig, updateAgroModulesConfig, KNOWN_AGRO_MODULE_IDS,
} from '../api/agro-modules.api';
import { fetchSystemStatus, type ProviderCheck, type TelegramBotCheck } from '../api/admin.api';
import { AI_PRESET_ORDER, providerLabel } from '../utils/aiProviders';
import { SecretsRotationSection } from '../components/shared/SecretsRotationSection';
import { ZoneCoordinatesSection } from '../components/shared/ZoneCoordinatesSection';
import { AiUsageSection } from '../components/shared/AiUsageSection';
import { ChatFeedbackSection } from '../components/shared/ChatFeedbackSection';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/auth.store';
import { useTenantStore } from '../store/tenant.store';
import { useLocaleStore } from '../store/locale.store';
import { useT, LOCALE_OPTIONS } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { usePermission } from '../hooks/usePermission';
import { ConfigFormField } from '../components/ConfigFormField';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { fetchNdviSettings } from '../api/ndvi-settings.api';
import { fetchRanchos } from '../api/rancho.api';
import { fetchScopedConfig, putScopedConfig } from '../api/config-scope.api';
import { statusDotColor, type DotColor } from '../utils/aiStatus';
import { GateRulesEditor } from '../components/GateRulesEditor';
import { fetchRoutines, fetchRoutineRuns, setRoutineEnabled, type RoutineItem } from '../api/routines.api';
import { PageTitle } from '../components/shared/PageTitle';
import { SubTabs } from '../components/shared/SubTabs';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' },

  tabContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },

  formField: { display: 'flex', flexDirection: 'column', gap: '4px' },
  formLabel: { fontSize: '12px', color: tokens.colorNeutralForeground3, fontWeight: '600' },

  providerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  providerName: { fontWeight: '600', fontSize: '13px', flex: 1 },
  providerOrderBtns: { display: 'flex', gap: '4px' },

  savedBadge: {
    color: tokens.colorStatusSuccessForeground1,
    fontSize: '12px',
    fontWeight: '600',
  },

  actionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  section: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  /* Idioma */
  langGrid: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  langBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 20px',
    borderRadius: tokens.borderRadiusMedium,
    border: `2px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: '90px',
    transition: 'all 0.15s',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  langBtnActive: {
    border: `2px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  langFlag:  { fontSize: '28px' },
  langLabel: { fontSize: '13px', fontWeight: '500' },

  /* Comportamiento del sistema */
  behaviorRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '12px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  behaviorLabel: { fontWeight: '600', fontSize: '14px' },
  behaviorDesc:  { fontSize: '12px', color: tokens.colorNeutralForeground3 },

  /* Status en vivo */
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '12px',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: '3px',
  },
  statusInfo: { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 },
  statusName: { fontWeight: '600', fontSize: '13px' },
  statusMsg:  { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  statusMeta: { fontSize: '11px', color: tokens.colorNeutralForeground3, marginTop: '2px' },
  refreshRow: { display: 'flex', alignItems: 'center', gap: '12px' },
});


// ─── Tab: Proveedores IA ──────────────────────────────────────────────────────

function ProvidersTab() {
  const s = useStyles();
  const t = useT();
  const ts = t.settings;
  const qc = useQueryClient();
  const canSuperAdmin = usePermission('admin:super');

  const [preset, setPreset] = useState<AiProvidersConfig['preset']>('SPEED');
  const [order, setOrder] = useState<AiProviderId[]>(AI_PRESET_ORDER.SPEED);
  const [disabled, setDisabled] = useState<AiProviderId[]>([]);
  const [saved, setSaved] = useState(false);

  const { fmtTimeOnly } = useIntlFormat();

  // Estado en vivo de proveedores (misma queryKey que la pestaña Sistema: una sola petición compartida).
  const { data: statusData, isLoading: statusLoading, isFetching: statusFetching, refetch: refetchStatus, dataUpdatedAt } = useQuery({
    queryKey: ['system-status'],
    queryFn:  fetchSystemStatus,
    staleTime: 4 * 60 * 1_000,
    retry: false,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['aiProvidersConfig'],
    queryFn: fetchAiProvidersConfig,
  });

  useEffect(() => {
    if (data) {
      setPreset(data.preset);
      setOrder(data.order);
      setDisabled(data.disabled);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload: AiProvidersConfig) => updateAiProvidersConfig(payload),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['aiProvidersConfig'] });
      setOrder(updated.order);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  function movesAllowed() {
    return preset === 'CUSTOM';
  }

  function moveProvider(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
  }

  function toggleDisabled(id: AiProviderId, checked: boolean) {
    setDisabled(checked ? disabled.filter(d => d !== id) : [...disabled, id]);
  }

  function handleSave() {
    mutation.mutate({ preset, order, disabled });
  }

  const lastUpdated = dataUpdatedAt
    ? fmtTimeOnly(dataUpdatedAt, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
    <div className={s.card} style={{ gap: '16px' }}>
      <div className={s.refreshRow}>
        <Text className={s.sectionTitle}>{t.settings.aiProviders}</Text>
        <Button
          size="small"
          appearance="subtle"
          onClick={() => { void refetchStatus(); }}
          disabled={statusFetching}
          icon={statusFetching ? <Spinner size="tiny" /> : undefined}
        >
          {statusFetching ? t.settings.checking : t.settings.refresh}
        </Button>
        {lastUpdated && (
          <Text style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
            {statusData?.fresh ? t.settings.cached : ''} {lastUpdated}
          </Text>
        )}
      </div>
      {statusLoading && <Spinner label={t.settings.checkingProviders} />}
      {statusData && (
        <div className={s.statusGrid}>
          <AiProviderRow name={providerLabel('gemini', statusData.models)}    check={statusData.providers.gemini} />
          <AiProviderRow name={providerLabel('openai', statusData.models)}    check={statusData.providers.openai} />
          <AiProviderRow name={providerLabel('anthropic', statusData.models)} check={statusData.providers.anthropic} />
          <AiProviderRow name={providerLabel('groq', statusData.models)}      check={statusData.providers.groq} />
          <AiProviderRow name={providerLabel('deepseek', statusData.models)}  check={statusData.providers.deepseek} />
          {statusData.providers.mistral && (
            <AiProviderRow name={providerLabel('mistral', statusData.models)} check={statusData.providers.mistral} />
          )}
          {statusData.providers.cerebras && (
            <AiProviderRow name={providerLabel('cerebras', statusData.models)} check={statusData.providers.cerebras} />
          )}
          {statusData.providers.openrouter && (
            <AiProviderRow name={providerLabel('openrouter', statusData.models)} check={statusData.providers.openrouter} />
          )}
          {statusData.providers.cloudflare && (
            <AiProviderRow name={providerLabel('cloudflare', statusData.models)} check={statusData.providers.cloudflare} />
          )}
        </div>
      )}
    </div>

    <AiUsageSection />

    {canSuperAdmin && <ChatFeedbackSection />}

    <div className={s.card}>
      <Text className={s.sectionTitle}>{ts.aiProvidersTitle}</Text>
      <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
        {ts.aiProvidersDesc}
      </Text>

      {isLoading ? (
        <Spinner size="small" />
      ) : (
        <>
          <div className={s.formField} style={{ maxWidth: '320px' }}>
            <span className={s.formLabel}>{ts.aiPresetLabel}</span>
            <Dropdown
              value={
                preset === 'COST' ? ts.aiPresetCost :
                preset === 'SPEED' ? ts.aiPresetSpeed : ts.aiPresetCustom
              }
              selectedOptions={[preset]}
              onOptionSelect={(_, d) => {
                const next = d.optionValue as AiProvidersConfig['preset'];
                setPreset(next);
                if (next !== 'CUSTOM') {
                  setOrder(AI_PRESET_ORDER[next]);
                }
              }}
            >
              <Option value="SPEED">{ts.aiPresetSpeed}</Option>
              <Option value="COST">{ts.aiPresetCost}</Option>
              <Option value="CUSTOM">{ts.aiPresetCustom}</Option>
            </Dropdown>
          </div>

          <div>
            {order.map((id, index) => (
              <div key={id} className={s.providerRow}>
                <Checkbox
                  checked={!disabled.includes(id)}
                  onChange={(_, d) => toggleDisabled(id, d.checked === true)}
                  label={ts.aiProviderEnabled}
                />
                <span className={s.providerName}>
                  {index + 1}. {providerLabel(id, statusData?.models)}
                </span>
                {movesAllowed() && (
                  <div className={s.providerOrderBtns}>
                    <Button
                      size="small"
                      appearance="subtle"
                      disabled={index === 0}
                      onClick={() => moveProvider(index, -1)}
                    >
                      {ts.aiMoveUp}
                    </Button>
                    <Button
                      size="small"
                      appearance="subtle"
                      disabled={index === order.length - 1}
                      onClick={() => moveProvider(index, 1)}
                    >
                      {ts.aiMoveDown}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={s.actionBar}>
            <Button
              appearance="primary"
              size="small"
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              {ts.aiProvidersSave}
            </Button>
            {saved && <span className={s.savedBadge}>✓ {ts.aiProvidersSaved}</span>}
          </div>
        </>
      )}
    </div>
    </div>
  );
}

// ─── Tab: Idioma (fusionado desde SettingsPage) ────────────────────────────────

// ─── Tab: Sistema — status en vivo (fusionado desde SettingsPage) ─────────────

interface SystemVersion {
  backend:    string;
  commit:     string;
  builtAt:    string;
  frontend?:  { version: string; commit: string };
  miniapp?:   { version: string; commit: string };
  api:        { version: string; env: string };
  timestamp:  string;
}

async function fetchSystemVersion(): Promise<SystemVersion> {
  const { data } = await apiClient.get<SystemVersion>('/api/version');
  return data;
}

const FRONTEND_VERSION = import.meta.env.VITE_APP_VERSION ?? '—';
const FRONTEND_BUILD   = import.meta.env.VITE_BUILD_DATE  ?? '—';
const FRONTEND_COMMIT  = import.meta.env.VITE_GIT_COMMIT  ?? '—';


const DOT_COLORS: Record<DotColor, string> = {
  green:  '#22c55e',
  yellow: '#f59e0b',
  red:    '#ef4444',
  gray:   '#9ca3af',
};

function useStatusLabel() {
  const t = useT();
  const MAP: Record<string, string> = {
    ok:            t.settings.statusOk,
    rate_limited:  t.settings.statusRateLimited,
    no_credits:    t.settings.statusNoCredits,
    no_webhook:    t.settings.statusNoWebhook,
    privacy_on:    t.settings.statusPrivacyOn,
    error:         t.settings.statusError,
    unconfigured:  t.settings.statusUnconfigured,
    disabled:      t.settings.statusDisabled,
    timeout:       t.settings.statusTimeout,
  };
  return (status: string) => MAP[status] ?? status;
}

function AiProviderRow({ name, check }: { name: string; check: ProviderCheck }) {
  const s = useStyles();
  const statusLabel = useStatusLabel();
  const color = DOT_COLORS[statusDotColor(check.status)];
  const latency = check.latencyMs != null ? `${check.latencyMs} ms` : '';
  const sub = check.message ?? (latency || statusLabel(check.status));

  return (
    <div className={s.statusItem}>
      <div className={s.statusDot} style={{ backgroundColor: color }} />
      <div className={s.statusInfo}>
        <span className={s.statusName}>{name}</span>
        <span className={s.statusMsg}>{sub}</span>
        {latency && check.message && <span className={s.statusMeta}>{latency}</span>}
      </div>
    </div>
  );
}

function TelegramBotRow({ name, check }: { name: string; check: TelegramBotCheck }) {
  const s = useStyles();
  const t = useT();
  const statusLabel = useStatusLabel();
  const color = DOT_COLORS[statusDotColor(check.status)];
  const handle = check.username ? `@${check.username}` : '';
  const sub = check.status === 'ok'
    ? `${handle} · ${t.settings.webhookActive}`
    : check.status === 'privacy_on'
    ? `${handle} · ${t.settings.privacyOnHint}`
    : check.status === 'no_webhook'
    ? `${handle} · ${t.settings.noWebhookRegistered}`
    : check.lastError ?? statusLabel(check.status);

  return (
    <div className={s.statusItem}>
      <div className={s.statusDot} style={{ backgroundColor: color }} />
      <div className={s.statusInfo}>
        <span className={s.statusName}>{name}</span>
        <span className={s.statusMsg}>{sub}</span>
        {check.pendingUpdates != null && check.pendingUpdates > 0 && (
          <span className={s.statusMeta}>⚠️ {check.pendingUpdates} {t.settings.pendingUpdates}</span>
        )}
      </div>
    </div>
  );
}

function VersionRow({ label, value, sub }: { label: string; value: string; sub?: string }) {
  const s = useStyles();
  return (
    <div className={s.statusItem}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
        <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>{label}</Text>
        <Text size={400} weight="semibold" style={{ fontFamily: 'monospace' }}>{value}</Text>
        {sub && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{sub}</Text>}
      </div>
    </div>
  );
}

/**
 * Comportamiento del sistema que se edita dentro de otras pestañas (bodega porcina y bloqueo de
 * presupuesto). No tiene botón propio: cada pestaña lo guarda con su único "Guardar".
 */
function useSystemBehaviorDraft() {
  const qc = useQueryClient();
  const [warehouseMode, setWarehouseMode] = useState<SystemBehaviorConfig['porcinoWarehouseMode']>('SHARED');
  const [budgetMode, setBudgetMode]       = useState<SystemBehaviorConfig['budgetBlockMode']>('SOFT');
  const [reportMode, setReportMode]       = useState<SystemBehaviorConfig['porcinoReportMode']>('INTEGRATED');

  const { data: behaviorConfig, isLoading } = useQuery({
    queryKey: ['systemBehavior'],
    queryFn:  fetchSystemBehavior,
  });

  useEffect(() => {
    if (behaviorConfig) {
      setWarehouseMode(behaviorConfig.porcinoWarehouseMode ?? 'SHARED');
      setBudgetMode(behaviorConfig.budgetBlockMode ?? 'SOFT');
      setReportMode(behaviorConfig.porcinoReportMode ?? 'INTEGRATED');
    }
  }, [behaviorConfig]);

  const behaviorMut = useMutation({
    mutationFn: (data: Partial<SystemBehaviorConfig>) => updateSystemBehavior(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['systemBehavior'] }),
  });

  return {
    isLoading,
    warehouseMode, setWarehouseMode,
    budgetMode, setBudgetMode,
    reportMode, setReportMode,
    saveWarehouse: () => behaviorMut.mutateAsync({ porcinoWarehouseMode: warehouseMode }),
    saveBudget:    () => behaviorMut.mutateAsync({ budgetBlockMode: budgetMode }),
    saveReport:    () => behaviorMut.mutateAsync({ porcinoReportMode: reportMode }),
  };
}

function WarehouseModeField({ value, onChange }: {
  value: SystemBehaviorConfig['porcinoWarehouseMode'];
  onChange: (v: SystemBehaviorConfig['porcinoWarehouseMode']) => void;
}) {
  const s  = useStyles();
  const ts = useT().settings;
  return (
    <div className={s.behaviorRow} style={{ borderBottom: 'none' }}>
      <span className={s.behaviorLabel}>{ts.warehouseMode}</span>
      <span className={s.behaviorDesc}>{ts.warehouseModeDesc}</span>
      <RadioGroup value={value} onChange={(_, d) => onChange(d.value as SystemBehaviorConfig['porcinoWarehouseMode'])} layout="horizontal">
        <Radio value="SHARED"   label={ts.warehouseShared} />
        <Radio value="SEPARATE" label={ts.warehouseSeparate} />
        <Radio value="BOTH"     label={ts.warehouseBoth} />
      </RadioGroup>
    </div>
  );
}

function ReportModeField({ value, onChange }: {
  value: SystemBehaviorConfig['porcinoReportMode'];
  onChange: (v: SystemBehaviorConfig['porcinoReportMode']) => void;
}) {
  const s  = useStyles();
  const ts = useT().settings;
  return (
    <div className={s.behaviorRow} style={{ borderBottom: 'none' }}>
      <span className={s.behaviorLabel}>{ts.reportMode}</span>
      <span className={s.behaviorDesc}>{ts.reportModeDesc}</span>
      <RadioGroup value={value} onChange={(_, d) => onChange(d.value as SystemBehaviorConfig['porcinoReportMode'])} layout="horizontal">
        <Radio value="INTEGRATED" label={ts.reportIntegrated} />
        <Radio value="SEPARATE"   label={ts.reportSeparate} />
      </RadioGroup>
    </div>
  );
}

function BudgetBlockField({ value, onChange }: {
  value: SystemBehaviorConfig['budgetBlockMode'];
  onChange: (v: SystemBehaviorConfig['budgetBlockMode']) => void;
}) {
  const s  = useStyles();
  const ts = useT().settings;
  return (
    <div className={s.behaviorRow} style={{ borderBottom: 'none' }}>
      <span className={s.behaviorLabel}>{ts.budgetBlock}</span>
      <span className={s.behaviorDesc}>{ts.budgetBlockDesc}</span>
      <RadioGroup value={value} onChange={(_, d) => onChange(d.value as SystemBehaviorConfig['budgetBlockMode'])} layout="horizontal">
        <Radio value="SOFT" label={ts.budgetBlockSoft} />
        <Radio value="HARD" label={ts.budgetBlockHard} />
      </RadioGroup>
    </div>
  );
}

function SystemInfoTab() {
  const s = useStyles();
  const t = useT();
  const { fmtTimeOnly } = useIntlFormat();
  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['system-status'],
    queryFn:  fetchSystemStatus,
    staleTime: 4 * 60 * 1_000, // 4 min — ligeramente menor al caché del backend (5 min)
    retry: false,
  });

  const { data: versionData, isLoading: versionLoading, isError: versionError, refetch: refetchVersion } = useQuery({
    queryKey: ['system-version'],
    queryFn:  fetchSystemVersion,
    staleTime: 60_000,
  });

  const lastUpdated = dataUpdatedAt
    ? fmtTimeOnly(dataUpdatedAt, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
      <div className={s.card}>
        <Text className={s.sectionTitle}>{t.settings.frontendSection}</Text>
        <VersionRow label={t.settings.version} value={`v${FRONTEND_VERSION}`} sub={`Commit: ${FRONTEND_COMMIT} · Build: ${FRONTEND_BUILD}`} />
      </div>
      <div className={s.card}>
        <Text className={s.sectionTitle}>{t.settings.backendSection}</Text>
        {versionLoading && <Spinner size="small" label={t.settings.checkingVersion} />}
        {versionError   && <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{t.settings.versionError}</Text>}
        {versionData && (
          <VersionRow
            label={t.settings.fieldBotLabel}
            value={`v${versionData.backend}`}
            sub={`Commit: ${versionData.commit} · Build: ${versionData.builtAt} · Env: ${versionData.api.env}`}
          />
        )}
      </div>
      <div className={s.card}>
        <Text className={s.sectionTitle}>{t.settings.miniappSection}</Text>
        {versionData && (
          <VersionRow
            label={t.settings.miniappLabel}
            value={`v${versionData.miniapp?.version ?? '—'}`}
            sub={`Commit: ${versionData.miniapp?.commit ?? '—'}`}
          />
        )}
      </div>
    </div>
    <div className={s.card} style={{ gap: '24px' }}>
      <div className={s.refreshRow}>
        <Text className={s.sectionTitle}>{t.settings.systemStatus}</Text>
        <Button
          size="small"
          appearance="subtle"
          onClick={() => { refetch(); refetchVersion(); }}
          disabled={isFetching}
          icon={isFetching ? <Spinner size="tiny" /> : undefined}
        >
          {isFetching ? t.settings.checking : t.settings.refresh}
        </Button>
        {lastUpdated && (
          <Text style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
            {data?.fresh ? t.settings.cached : ''} {lastUpdated}
          </Text>
        )}
      </div>

      {isLoading && <Spinner label={t.settings.checkingProviders} />}

      {data && (
        <>
          <Text className={s.sectionTitle} style={{ fontSize: '12px', marginBottom: '-8px' }}>
            {t.settings.telegramBots}
          </Text>
          <div className={s.statusGrid}>
            <TelegramBotRow name="Bot de Campo"   check={data.telegram.fieldBot} />
          </div>
          <SecretsRotationSection onRotated={() => { refetch(); }} highlightKey={data.telegram.fieldBot.status === 'ok' ? undefined : 'TELEGRAM_BOT_TOKEN'} />
        </>
      )}

      <hr style={{ border: 'none', borderTop: `1px solid ${tokens.colorNeutralStroke1}`, margin: 0 }} />

      <ZoneCoordinatesSection />

      <hr style={{ border: 'none', borderTop: `1px solid ${tokens.colorNeutralStroke1}`, margin: 0 }} />

    </div>

    </div>
  );
}

// ─── Shared: barra de acciones de un tab de config ─────────────────────────────

function ConfigActionBar({
  onSave, isSaving, saved, errorMessage, saveLabel, savedLabel,
}: {
  onSave: () => void; isSaving: boolean; saved: boolean; errorMessage: string | null;
  saveLabel: string; savedLabel: string;
}) {
  const s = useStyles();
  return (
    <div className={s.actionBar}>
      <Button appearance="primary" size="small" onClick={onSave} disabled={isSaving}>
        {saveLabel}
      </Button>
      {saved && <span className={s.savedBadge}>✓ {savedLabel}</span>}
      {errorMessage && <Text style={{ color: tokens.colorPaletteRedForeground1, fontSize: '12px' }}>{errorMessage}</Text>}
    </div>
  );
}

// ─── Tab: Scouting ────────────────────────────────────────────────────────

function ScoutingTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'scouting.severityLowPct': 20,
    'scouting.severityMediumPct': 40,
    'scouting.severityHighPct': 70,
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.scoutingTitle}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ConfigFormField type="number" label={t.scoutingLowLabel} description={t.scoutingLowDesc} value={values['scouting.severityLowPct']} onChange={v => updateField('scouting.severityLowPct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.scoutingMediumLabel} description={t.scoutingMediumDesc} value={values['scouting.severityMediumPct']} onChange={v => updateField('scouting.severityMediumPct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.scoutingHighLabel} description={t.scoutingHighDesc} value={values['scouting.severityHighPct']} onChange={v => updateField('scouting.severityHighPct', v)} min={0} max={100} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
    </div>
  );
}

// ─── Tab: Presupuesto ───────────────────────────────────────────────────────

function BudgetTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'budget.warnPct': 80,
    'budget.blockPct': 100,
    'budget.blockMode': 'SOFT',
  });
  const behavior = useSystemBehaviorDraft();

  if (isLoading || behavior.isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.budgetTitle}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ConfigFormField type="number" label={t.budgetWarnLabel} description={t.budgetWarnDesc} value={values['budget.warnPct']} onChange={v => updateField('budget.warnPct', v)} min={0} max={200} />
        <ConfigFormField type="number" label={t.budgetBlockLabel} description={t.budgetBlockDesc} value={values['budget.blockPct']} onChange={v => updateField('budget.blockPct', v)} min={0} max={200} />
        <ConfigFormField type="enum" label={t.budgetModeLabel} description={t.budgetModeDesc} value={values['budget.blockMode']} onChange={v => updateField('budget.blockMode', v)} options={['OFF', 'SOFT', 'HARD']} />
        <BudgetBlockField value={behavior.budgetMode} onChange={behavior.setBudgetMode} />
      </div>
      <ConfigActionBar
        onSave={() => { save(); behavior.saveBudget(); }}
        isSaving={isSaving}
        saved={saved}
        errorMessage={errorMessage}
        saveLabel={t.saveButton}
        savedLabel={t.savedBadge}
      />
    </div>
  );
}

// ─── Tab: Maquinaria ────────────────────────────────────────────────────────

function MachineryTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'machinery.alertYellowPct': 70, 'machinery.alertOrangePct': 85, 'machinery.alertRedPct': 95,
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.machineryTitle}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ConfigFormField type="number" label={t.machineryYellowLabel} description={t.machineryYellowDesc} value={values['machinery.alertYellowPct']} onChange={v => updateField('machinery.alertYellowPct', v)} min={0} max={200} />
        <ConfigFormField type="number" label={t.machineryOrangeLabel} description={t.machineryOrangeDesc} value={values['machinery.alertOrangePct']} onChange={v => updateField('machinery.alertOrangePct', v)} min={0} max={200} />
        <ConfigFormField type="number" label={t.machineryRedLabel} description={t.machineryRedDesc} value={values['machinery.alertRedPct']} onChange={v => updateField('machinery.alertRedPct', v)} min={0} max={200} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
    </div>
  );
}

// ─── Tab: Cosecha ───────────────────────────────────────────────────────────

function HarvestTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'harvest.packoutGoodPct': 70, 'harvest.packoutWarnPct': 60, 'harvest.packoutBadPct': 50, 'harvest.gradeLabels': 'A,B,C',
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.harvestTitle}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ConfigFormField type="number" label={t.harvestGoodLabel} description={t.harvestGoodDesc} value={values['harvest.packoutGoodPct']} onChange={v => updateField('harvest.packoutGoodPct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.harvestWarnLabel} description={t.harvestWarnDesc} value={values['harvest.packoutWarnPct']} onChange={v => updateField('harvest.packoutWarnPct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.harvestBadLabel} description={t.harvestBadDesc} value={values['harvest.packoutBadPct']} onChange={v => updateField('harvest.packoutBadPct', v)} min={0} max={100} />
        <ConfigFormField type="string" label={t.harvestGradesLabel} description={t.harvestGradesDesc} value={values['harvest.gradeLabels']} onChange={v => updateField('harvest.gradeLabels', v)} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
    </div>
  );
}

// ─── Tab: Localización ──────────────────────────────────────────────────────

function LocalizationTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'locale.currency': 'MXN', 'locale.dateFormat': 'dd/MM/yyyy', 'locale.decimalSeparator': '.', 'locale.speedUnit': 'km/h', 'locale.timezone': 'America/Mexico_City', 'locale.defaultLanguage': 'es', 'locale.phonePrefix': '+52', 'labor.workdayHours': 8,
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.localizationTitle}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ConfigFormField type="enum" label={t.currencyLabel} description={t.currencyDesc} value={values['locale.currency']} onChange={v => updateField('locale.currency', v)} options={['MXN', 'USD']} />
        <ConfigFormField type="enum" label={t.dateFormatLabel} description={t.dateFormatDesc} value={values['locale.dateFormat']} onChange={v => updateField('locale.dateFormat', v)} options={['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd']} />
        <ConfigFormField type="enum" label={t.decimalSeparatorLabel} description={t.decimalSeparatorDesc} value={values['locale.decimalSeparator']} onChange={v => updateField('locale.decimalSeparator', v)} options={['.', ',']} />
        <ConfigFormField type="enum" label={t.speedUnitLabel} description={t.speedUnitDesc} value={values['locale.speedUnit']} onChange={v => updateField('locale.speedUnit', v)} options={['km/h', 'mi/h']} />
        <ConfigFormField type="string" label={t.timezoneLabel} description={t.timezoneDesc} value={values['locale.timezone']} onChange={v => updateField('locale.timezone', v)} />
        <div>
          <Text weight="semibold" block>{t.defaultLangLabel}</Text>
          <Text size={200} block style={{ marginBottom: '8px' }}>{t.defaultLangDesc}</Text>
          <div className={s.langGrid}>
            {LOCALE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={mergeClasses(s.langBtn, values['locale.defaultLanguage'] === opt.value ? s.langBtnActive : undefined)}
                onClick={() => updateField('locale.defaultLanguage', opt.value)}
                type="button"
              >
                <span className={s.langFlag}><Flag iso={opt.flagIso} /></span>
                <span className={s.langLabel}>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
        <ConfigFormField type="string" label={t.phonePrefixLabel} description={t.phonePrefixDesc} value={values['locale.phonePrefix']} onChange={v => updateField('locale.phonePrefix', v)} />
        <ConfigFormField type="number" label={t.workdayHoursLabel} description={t.workdayHoursDesc} value={values['labor.workdayHours']} onChange={v => updateField('labor.workdayHours', v)} min={1} max={24} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
    </div>
  );
}

// ─── Tab: Comportamiento del Bot (Fase 3.3) ────────────────────────────────────

function BotBehaviorTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'notifications.dailyReminderHour': 20,
    'notifications.scheduledNotice.enabled': true,
    'notifications.scheduledNotice.intervalMinutes': 15,
  });
  const behavior = useSystemBehaviorDraft();

  if (isLoading || behavior.isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.botBehaviorTitle}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ConfigFormField type="number" label={t.reminderHourLabel} description={t.reminderHourDesc} value={values['notifications.dailyReminderHour']} onChange={v => updateField('notifications.dailyReminderHour', v)} min={0} max={23} />
        <ConfigFormField type="boolean" label={t.scheduledNoticeEnabledLabel} description={t.scheduledNoticeEnabledDesc} value={values['notifications.scheduledNotice.enabled']} onChange={v => updateField('notifications.scheduledNotice.enabled', v)} />
        <ConfigFormField type="number" label={t.scheduledNoticeIntervalLabel} description={t.scheduledNoticeIntervalDesc} value={values['notifications.scheduledNotice.intervalMinutes']} onChange={v => updateField('notifications.scheduledNotice.intervalMinutes', v)} min={5} max={1440} />
        <ReportModeField value={behavior.reportMode} onChange={behavior.setReportMode} />
      </div>
      <ConfigActionBar onSave={() => { save(); behavior.saveReport(); }} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
    </div>
  );
}

// ─── Tab: Cronogramas de Pronóstico (Fase 3.3) ─────────────────────────────────

function SchedulesTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'forecast.spread7Pct': 5, 'forecast.spread15Pct': 10, 'forecast.spread60Pct': 20,
    'forecast.confidence7Pct': 85, 'forecast.confidence15Pct': 65, 'forecast.confidence60Pct': 40,
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.schedulesTitle}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ConfigFormField type="number" label={t.spread7Label} description={t.spread7Desc} value={values['forecast.spread7Pct']} onChange={v => updateField('forecast.spread7Pct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.spread15Label} description={t.spread15Desc} value={values['forecast.spread15Pct']} onChange={v => updateField('forecast.spread15Pct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.spread60Label} description={t.spread60Desc} value={values['forecast.spread60Pct']} onChange={v => updateField('forecast.spread60Pct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.confidence7Label} description={t.confidence7Desc} value={values['forecast.confidence7Pct']} onChange={v => updateField('forecast.confidence7Pct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.confidence15Label} description={t.confidence15Desc} value={values['forecast.confidence15Pct']} onChange={v => updateField('forecast.confidence15Pct', v)} min={0} max={100} />
        <ConfigFormField type="number" label={t.confidence60Label} description={t.confidence60Desc} value={values['forecast.confidence60Pct']} onChange={v => updateField('forecast.confidence60Pct', v)} min={0} max={100} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
    </div>
  );
}

// ─── Tab: Compliance — Cadena de Frío (Fase 3.3) ───────────────────────────────

function ComplianceTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'coldChain.minTempC': 2, 'coldChain.maxTempC': 8,
    'compliance.mrlMissingDose': 'WARN',
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.complianceTitle}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <ConfigFormField type="number" label={t.coldMinLabel} description={t.coldMinDesc} value={values['coldChain.minTempC']} onChange={v => updateField('coldChain.minTempC', v)} min={-30} max={30} />
        <ConfigFormField type="number" label={t.coldMaxLabel} description={t.coldMaxDesc} value={values['coldChain.maxTempC']} onChange={v => updateField('coldChain.maxTempC', v)} min={-30} max={40} />
        <MrlMissingDoseField value={values['compliance.mrlMissingDose']} onChange={v => updateField('compliance.mrlMissingDose', v)} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
      <MrlMissingDoseRanchoOverrides />
    </div>
  );
}

const MRL_MISSING_DOSE_OPTIONS = ['BLOCK', 'WARN'] as const;

/** Selector de `compliance.mrlMissingDose` (empresa). Default WARN. */
function MrlMissingDoseField({ value, onChange }: { value: unknown; onChange: (v: unknown) => void }) {
  const t = useT().settings.adminConfig;
  return (
    <ConfigFormField
      type="enum"
      label={t.mrlMissingDoseLabel}
      description={t.mrlMissingDoseDesc}
      value={value ?? 'WARN'}
      onChange={onChange}
      options={MRL_MISSING_DOSE_OPTIONS}
      optionLabels={{ BLOCK: t.mrlMissingDoseBlock, WARN: t.mrlMissingDoseWarn }}
    />
  );
}

/** Override de `compliance.mrlMissingDose` por rancho (cascada empresa → rancho). */
function MrlMissingDoseRanchoOverrides() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const qc = useQueryClient();
  const canWrite = usePermission('config:write');
  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos'], queryFn: fetchRanchos });
  const effectiveQueries = useQueries({
    queries: ranchos.map(r => ({
      queryKey: ['mrl-missing-dose-rancho', r.ranchoId],
      queryFn: () => fetchScopedConfig('RANCHO', r.ranchoId),
      retry: false,
    })),
  });
  const mutation = useMutation({
    mutationFn: ({ ranchoId, value }: { ranchoId: string; value: string }) =>
      putScopedConfig('RANCHO', ranchoId, { 'compliance.mrlMissingDose': value }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['mrl-missing-dose-rancho', vars.ranchoId] });
    },
  });
  if (ranchos.length === 0) return null;
  return (
    <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="mrl-missing-dose-rancho-overrides">
      <Text className={s.sectionTitle}>{t.mrlMissingDoseRanchosTitle}</Text>
      <Text size={200}>{t.mrlMissingDoseRanchosDesc}</Text>
      {ranchos.map((r, i) => {
        const effective = effectiveQueries[i]?.data?.effective['compliance.mrlMissingDose']?.value;
        return (
          <ConfigFormField
            key={r.ranchoId}
            type="enum"
            label={r.name}
            disabled={!canWrite}
            value={effective === 'BLOCK' ? 'BLOCK' : 'WARN'}
            options={MRL_MISSING_DOSE_OPTIONS}
            optionLabels={{ BLOCK: t.mrlMissingDoseBlock, WARN: t.mrlMissingDoseWarn }}
            onChange={v => { if (canWrite) mutation.mutate({ ranchoId: r.ranchoId, value: String(v) }); }}
          />
        );
      })}
    </div>
  );
}

// ─── Tab: Feature Flags (Fase 3.3) ──────────────────────────────────────────────

/** Ranchos y alcance: qué se filtra por rancho activo y qué módulos de zonas están activos. */
function ScopeTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'inventory.scopeByRancho': false,
    'machinery.scopeByRancho': false,
    'modules.useFarmModules': true,
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.tabScope}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ConfigFormField type="boolean" label={t.inventoryScopeByRanchoLabel} description={t.inventoryScopeByRanchoDesc} value={values['inventory.scopeByRancho']} onChange={v => updateField('inventory.scopeByRancho', v)} />
        <ConfigFormField type="boolean" label={t.machineryScopeByRanchoLabel} description={t.machineryScopeByRanchoDesc} value={values['machinery.scopeByRancho']} onChange={v => updateField('machinery.scopeByRancho', v)} />
        <ConfigFormField type="boolean" label={t.useFarmModulesLabel} description={t.useFarmModulesDesc} value={values['modules.useFarmModules']} onChange={v => updateField('modules.useFarmModules', v)} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
    </div>
  );
}

/** Inventario y bodegas: descuento de stock, bloqueo de negativos y el modo de bodega porcina. */
function InventoryTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'logistics.trackingEnabled': false,
    'warehouse.autoDeductOnActivity': false,
    'warehouse.negativeStockIgnoreScope': 'WAREHOUSE',
  });

  const behavior = useSystemBehaviorDraft();

  if (isLoading || behavior.isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.tabInventory}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ConfigFormField type="boolean" label={t.trackingEnabledLabel} description={t.trackingEnabledDesc} value={values['logistics.trackingEnabled']} onChange={v => updateField('logistics.trackingEnabled', v)} />
        <ConfigFormField type="boolean" label={t.autoDeductLabel} description={t.autoDeductDesc} value={values['warehouse.autoDeductOnActivity']} onChange={v => updateField('warehouse.autoDeductOnActivity', v)} />
        <ConfigFormField type="enum" label={t.negativeStockIgnoreScopeLabel} description={t.negativeStockIgnoreScopeDesc} value={values['warehouse.negativeStockIgnoreScope']} onChange={v => updateField('warehouse.negativeStockIgnoreScope', v)} options={['WAREHOUSE', 'TENANT']} />
        <WarehouseModeField value={behavior.warehouseMode} onChange={behavior.setWarehouseMode} />
      </div>
      <ConfigActionBar
        onSave={() => { save(); behavior.saveWarehouse(); }}
        isSaving={isSaving}
        saved={saved}
        errorMessage={errorMessage}
        saveLabel={t.saveButton}
        savedLabel={t.savedBadge}
      />
    </div>
  );
}

/** Finanzas: anomalías y qué entra o sale en el cashflow proyectado. */
function CashflowTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'anomaly.autoDetectEnabled': true,
    'cashflow.includeBudgetOutflows': true,
    'cashflow.includeForecastInflows': true,
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.tabCashflow}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ConfigFormField type="boolean" label={t.anomalyAutoLabel} description={t.anomalyAutoDesc} value={values['anomaly.autoDetectEnabled']} onChange={v => updateField('anomaly.autoDetectEnabled', v)} />
        <ConfigFormField type="boolean" label={t.cashflowBudgetLabel} description={t.cashflowBudgetDesc} value={values['cashflow.includeBudgetOutflows']} onChange={v => updateField('cashflow.includeBudgetOutflows', v)} />
        <ConfigFormField type="boolean" label={t.cashflowForecastLabel} description={t.cashflowForecastDesc} value={values['cashflow.includeForecastInflows']} onChange={v => updateField('cashflow.includeForecastInflows', v)} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
    </div>
  );
}

/** Integraciones: NDVI de Agromonitoring, la empresa y sus overrides por rancho. */
function AgromonitoringTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'ndvi.enabled': false,
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.tabAgromonitoring}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ConfigFormField type="boolean" label={t.ndviEnabledLabel} description={t.ndviEnabledDesc} value={values['ndvi.enabled']} onChange={v => updateField('ndvi.enabled', v)} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
      <NdviRanchoOverrides />
    </div>
  );
}

// ─── Tab: Facturación electrónica (CFDI) ───────────────────────────────────────

/** Credenciales de Facty (API key + org): se guardan cifradas en el servidor y la key nunca se vuelve a mostrar. */
function FactyCredentialsSection() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const qc = useQueryClient();
  const canWrite = usePermission('config:write');
  const [apiKey, setApiKey] = useState('');
  const [org, setOrg] = useState('');
  const { data: status } = useQuery({
    queryKey: ['facty-credentials'],
    queryFn: async () => (await apiClient.get<{ configured: boolean; org?: string }>('/api/invoicing/credentials')).data,
  });
  const save = useMutation({
    mutationFn: () => apiClient.put('/api/invoicing/credentials', { apiKey: apiKey.trim(), org: org.trim() }),
    onSuccess: () => { setApiKey(''); setOrg(''); qc.invalidateQueries({ queryKey: ['facty-credentials'] }); },
  });
  const remove = useMutation({
    mutationFn: () => apiClient.delete('/api/invoicing/credentials'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['facty-credentials'] }),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px' }}>
      <Text className={s.sectionTitle}>{t.factyCredentialsTitle}</Text>
      <Text>{status?.configured ? `${t.factyCredentialsConfigured} (${status.org})` : t.factyCredentialsMissing}</Text>
      {canWrite && (
        <>
          <Input placeholder={t.factyOrgLabel} value={org} onChange={(_, d) => setOrg(d.value)} />
          <Input type="password" placeholder={t.factyApiKeyLabel} value={apiKey} onChange={(_, d) => setApiKey(d.value)} autoComplete="off" />
          {(save.isError || remove.isError) && <Text>{t.factyCredentialsError}</Text>}
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button appearance="primary" disabled={!apiKey.trim() || !org.trim() || save.isPending} onClick={() => save.mutate()}>{t.factyCredentialsSave}</Button>
            {status?.configured && <Button disabled={remove.isPending} onClick={() => remove.mutate()}>{t.factyCredentialsRemove}</Button>}
          </div>
        </>
      )}
    </div>
  );
}

/** Configuración de CFDI por empresa: no es un feature flag, es la conexión con Facty. */
function InvoicingTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { values, updateField, save, isLoading, isSaving, saved, errorMessage } = useTenantConfig({
    'invoicing.enabled': false,
    'invoicing.emitterRfc': '',
    'invoicing.emitterRegimen': '',
    'invoicing.stampingMode': 'sandbox',
  });

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.tabInvoicing}</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ConfigFormField type="boolean" label={t.invoicingEnabledLabel} description={t.invoicingEnabledDesc} value={values['invoicing.enabled']} onChange={v => updateField('invoicing.enabled', v)} />
        <ConfigFormField type="string" label={t.invoicingRfcLabel} description={t.invoicingRfcDesc} value={values['invoicing.emitterRfc']} onChange={v => updateField('invoicing.emitterRfc', v)} />
        <ConfigFormField type="string" label={t.invoicingRegimenLabel} description={t.invoicingRegimenDesc} value={values['invoicing.emitterRegimen']} onChange={v => updateField('invoicing.emitterRegimen', v)} />
        <ConfigFormField type="enum" label={t.invoicingModeLabel} description={t.invoicingModeDesc} value={values['invoicing.stampingMode']} onChange={v => updateField('invoicing.stampingMode', v)} options={['sandbox', 'production']} />
      </div>
      <ConfigActionBar onSave={() => save()} isSaving={isSaving} saved={saved} errorMessage={errorMessage} saveLabel={t.saveButton} savedLabel={t.savedBadge} />
      <FactyCredentialsSection />
    </div>
  );
}

/** Override de `ndvi.enabled` por rancho (sobrescribe el valor de la empresa). */
function NdviRanchoOverrides() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const qc = useQueryClient();
  const canWrite = usePermission('config:write');
  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos'], queryFn: fetchRanchos });
  const { data: effective } = useQuery({ queryKey: ['ndvi-settings-admin'], queryFn: fetchNdviSettings, retry: false });
  const mutation = useMutation({
    mutationFn: ({ ranchoId, value }: { ranchoId: string; value: boolean }) =>
      putScopedConfig('RANCHO', ranchoId, { 'ndvi.enabled': value }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ndvi-settings-admin'] });
      qc.invalidateQueries({ queryKey: ['ndvi-settings'] });
    },
  });
  if (ranchos.length === 0 || !effective) return null;
  return (
    <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }} data-testid="ndvi-rancho-overrides">
      <Text className={s.sectionTitle}>{t.ndviRanchosTitle}</Text>
      <Text size={200}>{t.ndviRanchosDesc}</Text>
      {ranchos.map(r => (
        <ConfigFormField
          key={r.ranchoId}
          type="boolean"
          label={r.name}
          disabled={!canWrite}
          value={effective.ranchos[r.ranchoId] ?? effective.tenantEnabled}
          onChange={v => { if (canWrite) mutation.mutate({ ranchoId: r.ranchoId, value: v === true }); }}
        />
      ))}
    </div>
  );
}

// ─── Tab: Módulos del sistema (Task 6) ─────────────────────────────────────────

function AgroModulesTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const { displayName } = useTenantStore();
  const agroModulesHelp = displayName
    ? t.agroModulesHelpWithCompany.replace('{company}', displayName)
    : t.agroModulesHelp;
  const qc = useQueryClient();

  const [enabledIds, setEnabledIds] = useState<string[]>([...KNOWN_AGRO_MODULE_IDS]);
  const [notifyDisabled, setNotifyDisabled] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['agro-modules-config'],
    queryFn: fetchAgroModulesConfig,
  });

  useEffect(() => {
    if (!data) return;
    setEnabledIds(
      data.enabledAgroModules === null
        ? [...KNOWN_AGRO_MODULE_IDS]
        : data.enabledAgroModules,
    );
    setNotifyDisabled(data.notifyDisabledModules);
  }, [data]);

  const mutation = useMutation({
    mutationFn: updateAgroModulesConfig,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agro-modules-config'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  function toggleModule(id: string, checked: boolean) {
    setEnabledIds(prev => checked ? [...prev, id] : prev.filter(m => m !== id));
  }

  function handleSave() {
    const allEnabled = KNOWN_AGRO_MODULE_IDS.every(id => enabledIds.includes(id))
      && enabledIds.length === KNOWN_AGRO_MODULE_IDS.length;
    mutation.mutate({
      enabledAgroModules: allEnabled ? null : enabledIds,
      notifyDisabledModules: notifyDisabled,
    });
  }

  if (isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.agroModulesTab}</Text>
      <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
        {agroModulesHelp}
      </Text>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {KNOWN_AGRO_MODULE_IDS.map(id => (
          <Checkbox
            key={id}
            checked={enabledIds.includes(id)}
            onChange={(_, d) => toggleModule(id, d.checked === true)}
            label={id}
          />
        ))}
      </div>

      <Switch
        checked={notifyDisabled}
        onChange={(_, d) => setNotifyDisabled(d.checked)}
        label={t.notifyDisabledSwitch}
      />

      <div className={s.actionBar}>
        <Button
          appearance="primary"
          size="small"
          onClick={handleSave}
          disabled={mutation.isPending}
        >
          {t.saveAgroModules}
        </Button>
        {saved && <span className={s.savedBadge}>✓ {t.savedBadge}</span>}
      </div>
    </div>
  );
}

// ─── Tab: Rutinas automáticas ──────────────────────────────────────────────────

function RoutinesTab() {
  const s = useStyles();
  const t = useT().settings.adminConfig;
  const qc = useQueryClient();
  const locale = useLocaleStore((st) => st.locale);
  const { displayName, tenantId } = useTenantStore();
  const canWrite = usePermission('config:write');
  const [toggleError, setToggleError] = useState(false);

  const routinesQ = useQuery({ queryKey: ['routines'], queryFn: fetchRoutines });
  const runsQ = useQuery({ queryKey: ['routine-runs'], queryFn: () => fetchRoutineRuns(50) });

  const mutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => setRoutineEnabled(id, enabled),
    onSuccess: () => { setToggleError(false); qc.invalidateQueries({ queryKey: ['routines'] }); },
    onError: () => setToggleError(true),
  });

  const routines = routinesQ.data ?? [];
  const runs = runsQ.data ?? [];
  const titleOf = (id: string) => routines.find((r) => r.id === id)?.title?.[locale] ?? id;
  const dayNames = [t.routinesDay1, t.routinesDay2, t.routinesDay3, t.routinesDay4, t.routinesDay5, t.routinesDay6, t.routinesDay7];
  const scheduleLabel = (r: RoutineItem) => r.schedule.isoWeekday
    ? t.routinesScheduleWeekly.replace('{day}', dayNames[r.schedule.isoWeekday - 1] ?? '').replace('{hour}', String(r.schedule.hourLocal))
    : t.routinesScheduleDaily.replace('{hour}', String(r.schedule.hourLocal));
  const statusLabel = (st: string) => (t as Record<string, string>)[`routinesStatus${st}`] ?? st;
  const cell: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', fontSize: '13px' };

  if (routinesQ.isLoading) return <div className={s.card}><Spinner size="small" /></div>;

  return (
    <div className={s.card}>
      <Text className={s.sectionTitle}>{t.routinesTitle}</Text>
      <Text size={200}>{t.routinesDesc}</Text>

      {routines.length === 0 ? (
        <Text size={200}>{t.routinesEmpty}</Text>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={cell}>{t.routinesColRoutine}</th>
              <th style={cell}>{t.routinesColSchedule}</th>
              <th style={cell}>{t.routinesColLastRun}</th>
              <th style={cell}>{t.routinesColEnabled}</th>
            </tr>
          </thead>
          <tbody>
            {routines.map((r) => (
              <tr key={r.id}>
                <td style={cell}>{titleOf(r.id)}</td>
                <td style={cell}>{scheduleLabel(r)}</td>
                <td style={cell}>{r.lastRun ? `${r.lastRun.runDate} · ${statusLabel(r.lastRun.status)}` : t.routinesNever}</td>
                <td style={cell}>
                  <Switch
                    aria-label={titleOf(r.id)}
                    checked={r.enabled}
                    disabled={!canWrite || mutation.isPending}
                    onChange={(_, d) => mutation.mutate({ id: r.id, enabled: d.checked })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {toggleError && <Text style={{ color: tokens.colorPaletteRedForeground1, fontSize: '12px' }}>{t.routinesToggleError}</Text>}

      <Text className={s.sectionTitle}>{t.routinesLogTitle}</Text>
      {runs.length === 0 ? (
        <Text size={200}>{t.routinesLogEmpty}</Text>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="routine-runs">
          <thead>
            <tr>
              <th style={cell}>{t.routinesColDate}</th>
              <th style={cell}>{t.routinesColCompany}</th>
              <th style={cell}>{t.routinesColRoutine}</th>
              <th style={cell}>{t.routinesColResult}</th>
              <th style={cell}>{t.routinesColError}</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.runKey}>
                <td style={cell}>{r.runDate}</td>
                <td style={cell}>{displayName ?? tenantId ?? r.tenantId}</td>
                <td style={cell}>{titleOf(r.routineId)}</td>
                <td style={cell}>{statusLabel(r.status)}</td>
                <td style={cell}>{r.errorMessage ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

type ConfigGroupId = 'scope' | 'inventory' | 'finance' | 'integrations' | 'system';
const CONFIG_TAB_GROUPS: { id: ConfigGroupId; tabs: string[] }[] = [
  { id: 'scope', tabs: ['scope', 'scouting', 'machinery', 'harvest', 'compliance'] },
  { id: 'inventory', tabs: ['inventory'] },
  { id: 'finance', tabs: ['budget', 'cashflow', 'schedules'] },
  { id: 'integrations', tabs: ['providers', 'agromonitoring', 'invoicing', 'botBehavior'] },
  { id: 'system', tabs: ['localization', 'agroModules', 'system', 'routines', 'gateRules'] },
];

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AdminConfigPage() {
  const s = useStyles();
  const t = useT();
  const { user } = useAuthStore();

  const isSuperAdmin = usePermission('admin:super') && user?.activeRole === 'SUPER_ADMIN';
  const canReadRoutines = usePermission('routines:read');
  const canEditGateRules = usePermission('attendance:policy');
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [tabInitialized, setTabInitialized] = useState(false);

  // `isSuperAdmin` depende de la matriz de permisos, que llega async — no se puede
  // decidir el tab inicial en el useState() de arriba (siempre montaría en falso).
  // Una vez resuelto, si es SUPER_ADMIN se aterriza en "providers" como antes;
  // el usuario puede navegar libremente después sin que esto lo vuelva a pisar.
  useEffect(() => {
    if (!tabInitialized && isSuperAdmin) {
      setSelectedTab('providers');
      setTabInitialized(true);
    }
  }, [isSuperAdmin, tabInitialized]);

  const ac = t.settings.adminConfig;

  const groups = CONFIG_TAB_GROUPS
    .map((g) => ({
      ...g,
      tabs: g.tabs.filter((id) => (id === 'routines' ? canReadRoutines : id === 'gateRules' ? canEditGateRules : isSuperAdmin)),
    }))
    .filter((g) => g.tabs.length > 0);
  const activeGroup = groups.find((g) => g.tabs.includes(selectedTab)) ?? groups[0];
  const tab = activeGroup && activeGroup.tabs.includes(selectedTab) ? selectedTab : (activeGroup?.tabs[0] ?? '');
  const tabLabels: Record<string, string> = {
    providers: ac.tabProviders, scouting: ac.tabScouting,
    budget: ac.tabBudget, machinery: ac.tabMachinery, harvest: ac.tabHarvest,
    localization: ac.tabLocalization, botBehavior: ac.tabBotBehavior, schedules: ac.tabSchedules,
    compliance: ac.tabCompliance, scope: ac.tabScope, inventory: ac.tabInventory, cashflow: ac.tabCashflow,
    agromonitoring: ac.tabAgromonitoring, invoicing: ac.tabInvoicing, agroModules: ac.tabAgroModules,
    system: ac.tabSystem, routines: ac.tabRoutines, gateRules: ac.tabGateRules,
  };
  const groupLabels: Record<ConfigGroupId, string> = {
    scope: ac.groupScope, inventory: ac.groupInventory, finance: ac.groupFinance,
    integrations: ac.groupIntegrations, system: ac.groupSystem,
  };

  if (!activeGroup) return null;

  return (
    <div className={s.root}>
      <PageTitle>{ac.pageTitle}</PageTitle>

      <div className={s.tabContainer}>
        {groups.length > 1 && (
          <SubTabs
            ariaLabel={ac.groupsLabel}
            selected={activeGroup.id}
            onSelect={(v) => {
              const g = groups.find((x) => x.id === v);
              if (g) setSelectedTab(g.tabs[0]);
            }}
            tabs={groups.map((g) => ({ value: g.id, label: groupLabels[g.id] }))}
          />
        )}
        <SubTabs
          selected={tab}
          onSelect={(v) => setSelectedTab(v)}
          tabs={activeGroup.tabs.map((id) => ({ value: id, label: tabLabels[id] }))}
        />

        {isSuperAdmin && selectedTab === 'providers' && <ProvidersTab />}
        {isSuperAdmin && selectedTab === 'scouting' && <ScoutingTab />}
        {isSuperAdmin && selectedTab === 'budget' && <BudgetTab />}
        {isSuperAdmin && selectedTab === 'machinery' && <MachineryTab />}
        {isSuperAdmin && selectedTab === 'harvest' && <HarvestTab />}
        {isSuperAdmin && selectedTab === 'localization' && <LocalizationTab />}
        {isSuperAdmin && selectedTab === 'botBehavior' && <BotBehaviorTab />}
        {isSuperAdmin && selectedTab === 'schedules' && <SchedulesTab />}
        {isSuperAdmin && selectedTab === 'compliance' && <ComplianceTab />}
        {isSuperAdmin && selectedTab === 'scope' && <ScopeTab />}
        {isSuperAdmin && selectedTab === 'inventory' && <InventoryTab />}
        {isSuperAdmin && selectedTab === 'cashflow' && <CashflowTab />}
        {isSuperAdmin && selectedTab === 'agromonitoring' && <AgromonitoringTab />}
        {isSuperAdmin && selectedTab === 'invoicing' && <InvoicingTab />}
        {isSuperAdmin && selectedTab === 'agroModules' && <AgroModulesTab />}
        {isSuperAdmin && selectedTab === 'system' && <SystemInfoTab />}
        {canReadRoutines && selectedTab === 'routines' && <RoutinesTab />}
        {canEditGateRules && selectedTab === 'gateRules' && <GateRulesEditor />}
      </div>
    </div>
  );
}
